import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Box,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Pagination,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import {
  blogPosts,
  blogCategories,
  getFeaturedPosts,
  getPostsByCategory,
  getRecentPosts,
  searchBlogPosts,
} from '../data/blogPosts';

const BlogList = () => {
  const { language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredPosts, setFilteredPosts] = useState(blogPosts);
  const [isSearching, setIsSearching] = useState(false);
  const postsPerPage = 6;

  const featuredPosts = getFeaturedPosts();
  const recentPosts = getRecentPosts(5);

  useEffect(() => {
    let posts = blogPosts;

    // Filter by category
    if (selectedCategory !== 'all') {
      posts = getPostsByCategory(selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      posts = searchBlogPosts(searchQuery, language);
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }

    setFilteredPosts(posts);
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, language]);

  // Pagination logic
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const truncateHTML = (html, maxLength = 150) => {
    const text = html.replace(/<[^>]*>/g, '');
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <>
      <SEOHead
        title={language === 'hi' ? 'ब्लॉग - आचार्य अभिषेक पांडेय' : 'Blog - Acharya Abhishek Pandey'}
        description={language === 'hi' ? 'ज्योतिष, तंत्र-मंत्र, रत्न विज्ञान और आध्यात्म पर नवीनतम लेख पढ़ें। दैनिक ज्योतिष टिप्स और उपाय।' : 'Read latest articles on astrology, tantra-mantra, gemstone science and spirituality. Daily astrology tips and remedies.'}
        keywords={language === 'hi' ? 'ज्योतिष ब्लॉग, तंत्र मंत्र, रत्न विज्ञान, आध्यात्म लेख' : 'astrology blog, tantra mantra, gemstone science, spirituality articles'}
        canonical="https://acharyaabhishekpandey.com/blog"
      />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              fontSize: { xs: '2rem', md: '3rem' },
              mb: 2,
            }}
          >
            {language === 'hi' ? 'आध्यात्मिक ब्लॉग' : 'Spiritual Blog'}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}
          >
            {language === 'hi'
              ? 'ज्योतिष, तंत्र-मंत्र, रत्न विज्ञान और आध्यात्म पर नवीनतम ज्ञान प्राप्त करें'
              : 'Get latest knowledge on astrology, tantra-mantra, gemstone science and spirituality'
            }
          </Typography>

          {/* Search Bar */}
          <Paper
            elevation={3}
            sx={{
              maxWidth: 500,
              mx: 'auto',
              mb: 4,
              borderRadius: 3,
            }}
          >
            <TextField
              fullWidth
              placeholder={language === 'hi' ? 'ब्लॉग खोजें...' : 'Search blog...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  '& fieldset': {
                    border: 'none',
                  },
                },
              }}
            />
          </Paper>
        </Box>

        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid size={{ xs: 12, lg: 8 }}>
            {/* Featured Posts Section */}
            {!isSearching && featuredPosts.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <StarIcon sx={{ color: 'warning.main', mr: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {language === 'hi' ? 'फीचर्ड पोस्ट्स' : 'Featured Posts'}
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  {featuredPosts.map((post) => (
                    <Grid size={{ xs: 12, md: 6 }} key={post.id}>
                      <Card 
                        elevation={4}
                        sx={{ 
                          height: '100%',
                          position: 'relative',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            transition: 'transform 0.3s ease-in-out',
                            boxShadow: 6,
                          },
                        }}
                      >
                        <Chip
                          label={language === 'hi' ? 'फीचर्ड' : 'Featured'}
                          color="warning"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            zIndex: 1,
                          }}
                        />
                        <CardActionArea component={Link} to={`/blog/${post.slug}`}>
                          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, lineHeight: 1.3 }}>
                              {post.title[language]}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                              {truncateHTML(post.content[language], 120)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                              <Chip
                                label={post.category[language]}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                <ScheduleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                <Typography variant="caption">
                                  {post.readTime[language]}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my: 4 }} />
              </Box>
            )}

            {/* Category Tabs */}
            <Paper elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
              <Tabs
                value={selectedCategory}
                onChange={handleCategoryChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ px: 2 }}
              >
                {blogCategories.map((category) => (
                  <Tab
                    key={category.id}
                    label={category.name[language]}
                    value={category.id}
                  />
                ))}
              </Tabs>
            </Paper>

            {/* Search Results Info */}
            {isSearching && (
              <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
                {language === 'hi' 
                  ? `"${searchQuery}" के लिए ${filteredPosts.length} परिणाम मिले`
                  : `Found ${filteredPosts.length} results for "${searchQuery}"`
                }
              </Typography>
            )}

            {/* Blog Posts Grid */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {currentPosts.map((post) => (
                <Grid size={{ xs: 12, md: 6 }} key={post.id}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        transition: 'transform 0.3s ease-in-out',
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardActionArea component={Link} to={`/blog/${post.slug}`}>
                      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, lineHeight: 1.3 }}>
                          {post.title[language]}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                          {post.excerpt[language]}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Chip
                            label={post.category[language]}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ mr: 1, mb: 1 }}
                          />
                          {post.tags.slice(0, 2).map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag[language]}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 1, mb: 1, fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            <CalendarIcon sx={{ fontSize: 14, mr: 0.5 }} />
                            <Typography variant="caption">
                              {formatDate(post.publishDate)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            <ScheduleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                            <Typography variant="caption">
                              {post.readTime[language]}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* No Results */}
            {filteredPosts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                  {language === 'hi' ? 'कोई पोस्ट नहीं मिली' : 'No posts found'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {language === 'hi' 
                    ? 'कृपया अन्य खोज शब्दों का प्रयास करें'
                    : 'Please try different search terms'
                  }
                </Typography>
              </Box>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              {/* Recent Posts */}
              <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <TrendingIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {language === 'hi' ? 'हाल की पोस्ट्स' : 'Recent Posts'}
                  </Typography>
                </Box>
                {recentPosts.map((post) => (
                  <Box key={post.id} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                    <Button
                      component={Link}
                      to={`/blog/${post.slug}`}
                      sx={{
                        textAlign: 'left',
                        textTransform: 'none',
                        width: '100%',
                        justifyContent: 'flex-start',
                        p: 0,
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                    >
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            lineHeight: 1.4,
                            mb: 1,
                            color: 'text.primary',
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {post.title[language]}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          <CalendarIcon sx={{ fontSize: 12, mr: 0.5 }} />
                          <Typography variant="caption">
                            {formatDate(post.publishDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </Button>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))}
              </Paper>

              {/* Categories */}
              <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                  {language === 'hi' ? 'श्रेणियां' : 'Categories'}
                </Typography>
                {blogCategories.filter(cat => cat.id !== 'all').map((category) => (
                  <Button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    sx={{
                      display: 'block',
                      textAlign: 'left',
                      textTransform: 'none',
                      width: '100%',
                      justifyContent: 'flex-start',
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: selectedCategory === category.id ? 'primary.light' : 'transparent',
                      color: selectedCategory === category.id ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor: selectedCategory === category.id ? 'primary.main' : 'grey.100',
                      },
                    }}
                  >
                    {category.name[language]}
                  </Button>
                ))}
              </Paper>

              {/* Ad Placement Area for Monetization */}
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  textAlign: 'center',
                  backgroundColor: 'grey.50',
                  minHeight: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    {language === 'hi' ? 'विज्ञापन स्थान' : 'Advertisement Space'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {language === 'hi' ? 'यहां आपका विज्ञापन हो सकता है' : 'Your ad could be here'}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default BlogList;